// Pure, framework-free mapping logic for the CSV/XLSX lead importer.
//
// Takes raw rows (objects keyed by the file's original headers) plus a column
// mapping (canonicalKey -> sourceHeader) and produces canonical rows the Rails
// importer assigns straight onto Company/Lead. Field transforms AND the dedup
// external_ids are computed here so they're unit-testable with Jest (no DOM).

// Trim a cell value; treat blank as null so the server stores NULL, not "".
export function clean(value) {
  if (value === null || value === undefined) return null
  const str = String(value).trim()
  return str === "" ? null : str
}

// A single full-name column wins; otherwise join first + last.
export function personName(c) {
  const full = clean(c.full_name)
  if (full) return full

  const parts = [c.first_name, c.last_name].map(clean).filter(Boolean)
  return parts.length ? parts.join(" ") : null
}

// A single location column wins; otherwise join city, state, country.
export function companyLocation(c) {
  const loc = clean(c.company_location)
  if (loc) return loc

  const parts = [c.company_city, c.company_state, c.company_country].map(clean).filter(Boolean)
  return parts.length ? parts.join(", ") : null
}

// "1,234 employees" -> 1234; blank/non-numeric -> null.
export function parseCompanySize(value) {
  const str = clean(value)
  if (!str) return null

  const digits = str.replace(/[^\d]/g, "")
  return digits === "" ? null : parseInt(digits, 10)
}

// LinkedIn URL -> path slug (e.g. "in/jane-doe", "company/acme").
function linkedinSlug(url) {
  const str = clean(url)
  if (!str) return null
  try {
    const u = new URL(str.includes("://") ? str : `https://${str}`)
    return u.pathname.replace(/^\/+|\/+$/g, "") || null
  } catch {
    return null
  }
}

// Website URL -> bare host without www.
function websiteHost(url) {
  const str = clean(url)
  if (!str) return null
  try {
    const u = new URL(str.includes("://") ? str : `https://${str}`)
    return u.hostname.replace(/^www\./, "").toLowerCase() || null
  } catch {
    return null
  }
}

// Stable dedup ids so re-imports don't duplicate. Null when nothing identifying.
export function leadExternalId(c) {
  const slug = linkedinSlug(c.linkedin)
  if (slug) return `csv/linkedin/${slug}`

  const email = clean(c.email)
  return email ? `csv/email/${email.toLowerCase()}` : null
}

export function companyExternalId(c) {
  const slug = linkedinSlug(c.company_linkedin)
  if (slug) return `csv/linkedin/${slug}`

  const host = websiteHost(c.company_website)
  return host ? `csv/domain/${host}` : null
}

// Resolve mapped values out of a raw row into the granular canonical keys.
function resolve(rawRow, mapping) {
  const c = {}
  for (const [key, header] of Object.entries(mapping || {})) {
    if (header) c[key] = rawRow[header]
  }
  return c
}

// Build one canonical row (model-facing keys) from a raw row + mapping.
export function toCanonical(rawRow, mapping) {
  const c = resolve(rawRow, mapping)
  return {
    name: personName(c),
    title: clean(c.title),
    email: clean(c.email),
    linkedin: clean(c.linkedin),
    external_id: leadExternalId(c),
    company_name: clean(c.company_name),
    company_website: clean(c.company_website),
    company_linkedin: clean(c.company_linkedin),
    company_location: companyLocation(c),
    company_size: parseCompanySize(c.company_size),
    company_external_id: companyExternalId(c),
  }
}

export function toCanonicalRows(rawRows, mapping) {
  return (rawRows || []).map((row) => toCanonical(row, mapping))
}
